import { contextInvalidContext } from './constants.ts';
import type { ContextDelta, ContextDeltaCommand } from './types/requests.ts';
import { exceptionInfo } from '../types.ts';
import type { WithRequired } from '../utilities.ts';
import type { PeerId, Restrictions } from '../../state/types/common.ts';
import type { Peer, State } from '../../state/types/state.ts';
import type { Context, ContextData, ContextId, Lifetime, Version, VersionedContext } from '../../state/types/context.ts';
import { check } from '../../restrictions.ts';
import { castDraft, Draft, produce } from 'immer';

export function contexts(state: State): ReadonlyArray<Context> {
    return Object.values(state.contexts || {});
}

export function addContext(state: State, context: Context): State {
    return produce(state, (draft: Draft<State>) => {
        draft.contexts = draft.contexts || {};
        draft.contexts[context.id] = castDraft(context);
    });
}

export function removeContext(state: State, contextId: string): State {
    return produce(state, (draft: Draft<State>) => {
        if (draft.contexts) {
            delete draft.contexts[contextId];
            if (Object.values(draft.contexts).length === 0) {
                delete draft.contexts;
            }
        }
    });
}


/**
 * Finds a context by name and peer user. Actually faster than filter/some
 */
export function contextByName(state: State, name: string | undefined, peer: Peer): VersionedContext | undefined {
    const peerUser = peer.identity.user;
    const isService = peer.options?.service;
    const contexts: Context[] = Object.values(state.contexts || {});

    let result: Context | undefined;
    // try to match against the same user
    result = contexts.find((c) => {
        return name === c.name && c.identity.user === peerUser
    });
    if (!result) {
        // try to match against a context that was created by a service
        result = contexts.find((c: Context) => {
            return name === c.name && (isService || c.options?.service)
        });
    }
    return result as VersionedContext | undefined;
}

export function contextById(state: State, contextId: string): Context | undefined {
    if (contextId) {
        return state.contexts?.[contextId];
    }
}

export function contextByIdThrows(domainUri: string, state: State, contextId: string): Context {
    const context = state.contexts?.[contextId];
    if (context) {
        return context;
    }
    throw exceptionInfo(`Unable to find context with id ${contextId}`, {uri: contextInvalidContext(domainUri)});
}

/**
 * Adds a peer as a member to a context
 */
export function addContextMember(state: State, context: Context | undefined, peerId: PeerId): State {
    if (context && peerId) {
        const existingMembers = context.members;
        if (existingMembers.has(peerId)) {
            return state;
        }
        return produce(state, (s: Draft<State>) => {
            s.contexts = s.contexts || {};
            s.contexts[context.id] = castDraft(produce(context, (c: Draft<Context>) => {
                c.members = c.members || new Set<PeerId>();
                c.members.add(peerId);
            }));
        });
    }
    return state;
}


/**
 * Removes a peer from the context members
 */
export function removeContextMember(state: State, context: Context, peerId: PeerId): [State, Context] {
    return produce([state, context], ([draftState, draftContext]: Draft<[State, Context]>) => {
        draftContext.members.delete(peerId);
        if (peerId === draftContext.owner) {
            delete draftContext.owner;
        }
        draftState.contexts = draftState.contexts || {};
        draftState.contexts[context.id] = draftContext;
    });
}


function setIn(targetData: { [name: string]: unknown }, targetPath: string[] | undefined, value: unknown) {
    if (!targetPath) {
        return value;
    }
    if (targetPath.length === 1) {
        targetData[targetPath[0]] = value;
    } else {
        let nextTargetData = targetData[targetPath[0]];
        if (nextTargetData === undefined || Array.isArray(nextTargetData) || typeof nextTargetData === 'number' || typeof nextTargetData === 'boolean') {
            nextTargetData = {};
        }
        targetData[targetPath[0]] = setIn(nextTargetData as { [name: string]: unknown }, targetPath.slice(1), value);
    }
    return targetData
}


function merge(a: object, b: object): object {
    return {...a, ...b};
}

function applyDeltaCmd(data: Draft<ContextData | undefined>, cmd: [string, unknown]): Draft<ContextData | undefined> {
    const [command, args] = cmd as ['removed' | 'added' | 'updated' | 'reset' | 'commands', unknown];
    switch (command) {
        case 'removed': {
            (args as Array<string>).forEach((removed: string) => {
                delete data?.[removed];
            });
            break;
        }
        case 'added': {
            data = Object.entries((args as Record<string, unknown>)).reduce((d, [k, v]) => {
                d[k] = v;
                return d;
            }, data ?? {});
            break;
        }
        case 'updated': {
            data = Object.entries((args as Record<string, unknown>)).reduce((cd, [k, v]) => {
                if (Array.isArray(v) && Array.isArray(cd[k])) {
                    cd[k] = v;
                } else if (v instanceof Object && cd[k] instanceof Object) {
                    cd[k] = merge(cd[k] as object, v);
                } else {
                    cd[k] = v;
                }
                return cd;
            }, data ?? {});
            break;
        }
        case 'reset': {
            if (args) {
                data = args as ContextData;
            }
            break;
        }
        case 'commands': {
            data = (args as Array<ContextDeltaCommand>).reduce((cd: { [name: string]: unknown } | undefined, cmd: ContextDeltaCommand) => {
                switch (cmd.type) {
                    case 'set': {
                        return setIn(cd ?? {}, stringToPath(cmd.path), cmd.value) as ContextData;
                    }
                    case 'remove': {
                        const p = stringToPath(cmd.path);
                        if (!p) {
                            return {};
                        } else {
                            let currentElement = cd;
                            for (let i = 0; i < p.length - 1; i++) {
                                let next;
                                if (typeof currentElement !== 'object') {
                                    return cd;
                                } else {
                                    next = currentElement?.[p[i]];
                                }
                                currentElement = next;
                            }
                            if (currentElement) {
                                delete currentElement[p[p.length - 1]];
                            }

                        }
                        return cd;
                    }
                }
            }, data);
            break;
        }
    }
    return data;
}


export function applyDelta(state: State, contextId: string, delta: ContextDelta, version: Version): State {

    return produce(state, (draft: Draft<Required<Pick<State, 'contexts'>>>) => {
        draft.contexts[contextId].data = Object.entries<unknown>(delta).reduce((data: Draft<ContextData | undefined>, [command, args]: [string, unknown]) => {
            return applyDeltaCmd(data, [command, args]);
        }, draft.contexts[contextId].data);
        draft.contexts[contextId].version = version;
    });
}

function stringToPath(s: string): string[] | undefined {
    if (s) {
        return s.split('.');
    }
}

export function toCtx(creator: Peer,
                      name: string,
                      data,
                      lifetime: Lifetime,
                      read_permissions: string | undefined,
                      write_permissions: string| undefined,
                      permissions: {read: Restrictions, write: Restrictions} | undefined,
                      ctxId: ContextId,
                      version: Version): VersionedContext {
    const identity = creator.identity;
    const options = creator.options;
    const ctx: WithRequired<Context, 'version'> = {
        id: ctxId,
        data: data ?? {},
        identity: identity,
        lifetime: lifetime,
        read_permissions,
        write_permissions,
        permissions,
        members: new Set<PeerId>(),
        version: version,
        name: name,
        creator: creator.id
    };
    if (options) {
        castDraft(ctx).options = options;
    }
    if (ctx.permissions === undefined) {
        delete castDraft(ctx).permissions;
    }
    if (ctx.read_permissions === undefined) {
        delete castDraft(ctx).read_permissions;
    }
    if (ctx.write_permissions === undefined) {
        delete castDraft(ctx).write_permissions;
    }
    return ctx;
}

export function nextVersion(context: Context): Version {
    let updates = context.version?.updates || 0;
    updates++;
    const timestamp = Date.now();
    return {updates, timestamp};
}

export function newVersion(): Version {
    return {updates: 0, timestamp: Date.now()};
}

export function isClusterContext(ctx: Context): boolean {
    return check(ctx.permissions?.read, [{type: ctx.local ? 'local' : 'cluster'}, ctx.identity], [{type: 'cluster'}, undefined]);
}
